/**
 * AfterClick 追踪模块
 *
 * 时序流程：
 * 用户点击 → 业务端发起 XHR → SDK 拦截响应 → 上报埋点 → 放行业务端回调（可能含页面跳转）
 */

import type {
  TrackData,
  CommonParams,
  DynamicEventConfig,
  DynamicElementConfig,
} from '../types'
import type { AfterClickInterceptStatus } from '../types/event'
import { refreshDynamicFields } from '../collectors/common'
import { collectClickEvent } from '../collectors/click'
import { resolveRoutePath, getPageId, resolveReferrerPage } from '../collectors/page'
import { Reporter } from '../reporter'
import type { RoutePathResolver, ReferrerResolver } from '../types/config'
import type { DynamicConfigManager } from '../core/dynamic-config'
import type { PageModule } from './page'
import { activateAfterClickIntercept, type CapturedXHRResponse } from '../utils/after-click-interceptor'
import { extractAttributes, collectMissingFields } from '../utils'
import { AFTER_CLICK_DEFAULTS } from '../core/constants'

export interface AfterClickModuleConfig {
  enabled: boolean
  debug?: boolean
  routePathResolver?: RoutePathResolver
  referrerResolver?: ReferrerResolver
  /** SDK 上报地址，用于排除拦截 */
  reportUrl?: string
}

/**
 * AfterClick 追踪模块
 *
 * 职责：
 * 1. 接收来自 ClickModule 的 afterClick 事件触发
 * 2. 激活 XHR 拦截器，等待匹配的业务请求完成
 * 3. 组装埋点数据并异步上报
 * 4. 上报完成后放行 XHR 响应给业务端
 */
export class AfterClickModule {
  private config: AfterClickModuleConfig
  private reporter: Reporter
  private commonParams: CommonParams
  private dynamicConfigManager?: DynamicConfigManager
  private pageModule?: PageModule

  constructor(
    config: AfterClickModuleConfig,
    reporter: Reporter,
    commonParams: CommonParams,
    dynamicConfigManager?: DynamicConfigManager,
    pageModule?: PageModule
  ) {
    this.config = config
    this.reporter = reporter
    this.commonParams = commonParams
    this.dynamicConfigManager = dynamicConfigManager
    this.pageModule = pageModule
  }

  /**
   * 更新公共参数引用
   */
  updateCommonParams(commonParams: CommonParams): void {
    this.commonParams = commonParams
  }

  /**
   * 处理 afterClick 事件
   *
   * 流程：
   * 1. 激活 XHR 拦截器（监听 elementApiPath）
   * 2. 业务端 XHR 请求完成时，捕获响应数据
   * 3. 组装并异步上报埋点数据
   * 4. 上报完成后返回，拦截器随即放行业务端回调
   *
   * @param event 原始点击事件
   * @param eleConfig 元素配置
   * @param eventConfig 事件配置（含 elementApiPath）
   */
  handleAfterClick(
    event: MouseEvent,
    eleConfig: DynamicElementConfig | null,
    eventConfig: DynamicEventConfig | null,
  ): void {
    if (!this.config.enabled) return

    // 从 ELEMENT_AFTER 字段中提取目标 API URL
    const targetApiUrl = eventConfig?.fields?.find(f => f.apiUrl)?.apiUrl
    if (!targetApiUrl) {
      this.log('afterClick fields 中未找到 apiUrl，跳过拦截')
      return
    }

    const timeout = eventConfig?.timeout ?? AFTER_CLICK_DEFAULTS.TIMEOUT
    const startTime = Date.now()

    this.log(`激活 XHR 拦截，目标 URL: ${targetApiUrl}，超时: ${timeout}ms`)

    // 激活拦截器
    activateAfterClickIntercept({
      targetApiUrl,
      timeout,
      excludeUrl: this.config.reportUrl,
      onCapture: async (response: CapturedXHRResponse) => {
        const duration = Date.now() - startTime
        this.log(`捕获到响应，状态: ${response.status}，耗时: ${duration}ms`)

        // 判断上报状态
        const interceptStatus: AfterClickInterceptStatus =
          response.status >= 200 && response.status < 400 ? 'success' : 'error'

        // 异步上报（等待完成，超时由外层 Promise.race 控制）
        await this.reportAfterClick(event, eleConfig, eventConfig, response, interceptStatus, duration)
      },
    })
  }

  /**
   * 组装并异步上报 afterClick 埋点数据
   */
  private async reportAfterClick(
    event: MouseEvent,
    eleConfig: DynamicElementConfig | null,
    eventConfig: DynamicEventConfig | null,
    response: CapturedXHRResponse,
    interceptStatus: AfterClickInterceptStatus,
    duration: number,
  ): Promise<void> {
    const clickParams = collectClickEvent(event)
    const common = refreshDynamicFields(this.commonParams)

    // 获取页面级参数
    const pageParams = this.pageModule?.getPageParams() ?? null



    // 从 ELEMENT_AFTER 字段中提取目标 API URL
    const targetApiUrl = eventConfig?.fields?.find(f => f.apiUrl)?.apiUrl || ''

    // 将拦截到的 XHR 响应包装成 apis 数组格式，供 extractAttributes 处理
    const capturedApi = {
      id: `afterclick_${Date.now()}`,
      url: targetApiUrl,
      responseBody: response.responseText,
      status: response.status,
      type: 'xhr',
    }

    // 分离 ELEMENT_AFTER 和非 ELEMENT_AFTER 字段
    const allFields = eventConfig?.fields || []
    const afterFields = allFields.filter(f => f.bindType === 'ELEMENT_AFTER')
    const normalFields = allFields.filter(f => f.bindType !== 'ELEMENT_AFTER')

    // ELEMENT_AFTER 字段：从拦截到的 XHR 响应中提取
    const afterAttrs = extractAttributes(afterFields, {
      pageParams: pageParams || {},
      eleParams: clickParams.clickEleParams,
      apis: [capturedApi],
    })

    // 非 ELEMENT_AFTER 字段：从页面已有网络记录中提取（参照 reportClick 逻辑）
    const networkRecords = this.pageModule?.getCurrentPageRequests() ?? []
    const normalAttrs = extractAttributes(normalFields, {
      pageParams: pageParams || {},
      eleParams: clickParams.clickEleParams,
      apis: networkRecords,
    })

    // 合并两组 fieldList
    const attributesData = {
      fieldList: [...normalAttrs.fieldList, ...afterAttrs.fieldList],
    }

    // 收集 fieldList 中 value 为 null 或 undefined 的字段信息
    const missingFields = collectMissingFields(attributesData.fieldList)

    // 获取页面信息
    const routePath = resolveRoutePath(this.config.routePathResolver)
    const pageId = getPageId(routePath)
    const pageConfig = this.dynamicConfigManager?.getPageConfig(pageId)

    const data: TrackData = {
      common,
      event: {
        eventType: 'click',
        eventName: eventConfig?.eventName || '',
        eventCode: eventConfig?.eventCode || '',
        click: {
          eleId: eleConfig?.eleId,
          eleBusinessName: eleConfig?.eleBusinessName,
          clickPosition: clickParams?.clickPosition,
          clickScene: clickParams?.clickScene || '',
          isAfter: true,
        },
        page: {
          pageId: pageId,
          pageName: pageConfig?.pageName || '',
          currentUrl: window.location.href,
          referrerPage: resolveReferrerPage(this.config.referrerResolver),
        },
      },
      business: {
        attributes: attributesData,
        missingFields,
        eventResult: {
          // targetApiUrl: eventConfig?.elementApiPath || '',
          // responseStatus: response.status,
          // responseData,
          // interceptStatus,
          // duration,
        },
      },
    }

    this.log('上报 afterClick 数据', data)

    // 异步上报（等待服务器确认）
    await this.reporter.reportAsync(data)
  }

  /**
   * 调试日志
   */
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      if (data !== undefined) {
        console.log(
          '%c[Tracker AfterClick 调试] %c' + message,
          'background: #67C23A; color: #fff;',
          'color: inherit;',
          data,
        )
      } else {
        console.log(
          '%c[Tracker AfterClick 调试] %c' + message,
          'background: #67C23A; color: #fff;',
          'color: inherit;',
        )
      }
    }
  }
}
